import {
  Directive,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { SupervisionComponent } from "src/components/general components/supervision/supervision.component";
import { ApplicationButtonComponent } from "src/components/supervision components/application-button/application-button.component";
import { ChartComponent } from "src/components/supervision components/chart/chart.component";
import { CommandButtonComponent } from "src/components/supervision components/command-button/command-button.component";
import { GaugeComponent } from "src/components/supervision components/gauge/gauge.component";
import { ImageComponent } from "src/components/supervision components/image/image.component";
import { LabelComponent } from "src/components/supervision components/label/label.component";
import { ScenarioButtonComponent } from "src/components/supervision components/scenario-button/scenario-button.component";
import { ShapeComponent } from "src/components/supervision components/shape/shape.component";
import { SQLTableComponent } from "src/components/supervision components/sql-table/sql-table.component";
import { SupervisionButtonComponent } from "src/components/supervision components/supervision-button/supervision-button.component";
import { VirtualAccessComponent } from "src/components/supervision components/virtual-access/virtual-access.component";
import { VlcCameraComponent } from "src/components/supervision components/vlc-camera/vlc-camera.component";
import { GenericBasicGUI } from "src/model/class/component classes/generic-basic-gui";
import { SupervisionComponentAPI } from "src/model/class/component classes/supervision-component-api";
import { GuiComponent, HAlign } from "src/model/enum/enumCustomInterface";
import { SupervisionService } from "src/service/supervision.service";

@Directive({
    selector: "[appCustomInterfacePlaceholder]",
    standalone: false
})

/**
 * The CustomInterfacePlaceholderDirective is used to indicate the container in which
 * the custom interface components must be dinamically created.
 * In the page Supervision we have a ng-template (an empty tag) with this directive.
 * Inside that tag will be created the custom interface components.
 * The important thing is the ViewContainerRef, which return the container associated to the html tag where the directive is applied.
 * With method drawCustomInterfaceComponents it render all supervision element in supervision graphic list
 */
export class CustomInterfacePlaceholderDirective {
  private _componentCreated: any[] = [];

  constructor(
    private _templateRef: TemplateRef<any>,
    private _viewContainerRef: ViewContainerRef,
    private _supervisionService: SupervisionService
  ) {
    _viewContainerRef.constructor.name === "ViewContainerRef_"; // true
  }

  public destroyCustomInterfaceComponents(
    listComponents: Array<SupervisionComponentAPI>
  ) {
    const listVisibleComponent = this.getOnlyVisibleComponent(listComponents);
    const componentsHostViewRef = this._viewContainerRef;

    componentsHostViewRef.clear();

    for (var index in this._componentCreated) {
      this._componentCreated[index].destroy();
      this._componentCreated[index].onDestroy();
    }
  }

  /**
   * Draw supervision components elements by initial API component list
   *
   * @param listComponents List component API to generate element
   * @param showInDesktopVersion Handle if component is in desktop version or list version
   * @param horizontalAlignment Horizontal aligment of component
   * @param boldForced Force bold property in component
   */
  public drawCustomInterfaceComponents(
    listComponents: Array<SupervisionComponentAPI>,
    showInDesktopVersion: boolean = true,
    horizontalAlignment: HAlign = HAlign.CENTER,
    boldForced: boolean = false
  ): void {
    const listVisibleComponent = this.getOnlyVisibleComponent(listComponents);
    const componentsHostViewRef = this._viewContainerRef;

    componentsHostViewRef.clear();

    // forEach component in the custom interface create the factory of the component based on the 'code' property
    for (const component of listVisibleComponent) {
      const compFactory = this.getComponentFactoryByComponentCode(
        component.code
      );

      // if the component is recognized and the factory has been created we create the real component in the html and load into it the
      // relative API object with the command and state addresses
      if (compFactory) {
        const componentRef = componentsHostViewRef.createComponent(compFactory);
        const componentInstanceRef = componentRef.instance as GenericBasicGUI;

        this._componentCreated.push(componentRef);

        componentInstanceRef.ComponentAPI = component;
        componentInstanceRef.ShowingInDesktopVersion = showInDesktopVersion;
        componentInstanceRef.hAlignment = horizontalAlignment;
        componentInstanceRef.boldForced = boldForced;
      }
    }
  }

  /**
   * Get component factory instance class by supervision component code
   *
   * @param componentCode Component code to get factory
   * @returns
   */
  private getComponentFactoryByComponentCode(componentCode: string): any {
    let compFactory: any = null;

    switch (componentCode) {
      case GuiComponent.LABEL:
        compFactory =
          this._viewContainerRef.createComponent(
            LabelComponent
          );
        break;
      case GuiComponent.COMMAND_BUTTON:
        compFactory = this._viewContainerRef.createComponent(
          CommandButtonComponent
        );
        break;
      case GuiComponent.APPLICATION_BUTTON:
        compFactory = this._viewContainerRef.createComponent(
          ApplicationButtonComponent
        );
        break;
      case GuiComponent.SCENARIO_BUTTON:
        compFactory = this._viewContainerRef.createComponent(
          ScenarioButtonComponent
        );
        break;
      case GuiComponent.SUPERVISION_BUTTON:
        compFactory = this._viewContainerRef.createComponent(
          SupervisionButtonComponent
        );
        break;
      case GuiComponent.IMAGE:
        compFactory =
          this._viewContainerRef.createComponent(
            ImageComponent
          );
        break;
      case GuiComponent.SHAPE:
        compFactory =
          this._viewContainerRef.createComponent(
            ShapeComponent
          );
        break;
      case GuiComponent.GAUGE:
        compFactory =
          this._viewContainerRef.createComponent(
            GaugeComponent
          );
        break;
      case GuiComponent.VLC_CAMERA:
        compFactory =
          this._viewContainerRef.createComponent(
            VlcCameraComponent
          );
        break;
      case GuiComponent.VIRTUAL_ACCESS:
        compFactory = this._viewContainerRef.createComponent(
          VirtualAccessComponent
        );
        break;
      case GuiComponent.VALUE_CHART:
        compFactory =
          this._viewContainerRef.createComponent(
            ChartComponent
          );
        break;
      case GuiComponent.SQL_TABLE:
        compFactory =
          this._viewContainerRef.createComponent(
            SQLTableComponent
          );
      default:
        break;
    }

    return compFactory;
  }

  /**
   * Return only visible component by full supervision list
   *
   * @param listComponents List componenent to filter
   * @returns
   */
  private getOnlyVisibleComponent(
    listComponents: Array<SupervisionComponentAPI>
  ): Array<SupervisionComponentAPI> {
    return listComponents.filter((component: SupervisionComponentAPI) =>
      this._supervisionService.isComponentVisible(component.visible)
    );
  }
}
